import { CdkDrag, CdkDragDrop, CdkDropList, CdkDropListGroup, moveItemInArray } from '@angular/cdk/drag-drop';
import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { AsyncPipe, DecimalPipe, NgClass } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  ElementRef,
  HostListener,
  inject,
  Input,
  OnDestroy,
  OnInit,
  signal,
  ViewChild
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormArray, FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import {
  MatAutocomplete,
  MatAutocompleteSelectedEvent,
  MatAutocompleteTrigger,
  MatOption
} from '@angular/material/autocomplete';
import { MatButton } from '@angular/material/button';
import { MatChipGrid, MatChipInput, MatChipRemove, MatChipRow } from '@angular/material/chips';
import { MatDialog } from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import { MatFormField, MatInput, MatPrefix } from '@angular/material/input';
import { MatMenu, MatMenuItem, MatMenuTrigger } from '@angular/material/menu';
import { MatSelect } from '@angular/material/select';
import { MatSlideToggle } from '@angular/material/slide-toggle';
import { MatSort, MatSortHeader, Sort } from '@angular/material/sort';
import {
  MatCell,
  MatCellDef,
  MatColumnDef,
  MatHeaderCell,
  MatHeaderCellDef,
  MatHeaderRow,
  MatHeaderRowDef,
  MatRow,
  MatRowDef,
  MatTable
} from '@angular/material/table';
import { MatTooltip } from '@angular/material/tooltip';
import { Router } from '@angular/router';
import { color } from 'chroma.ts';
import {
  ApexAxisChartSeries,
  ApexChart,
  ApexDataLabels,
  ApexFill,
  ApexGrid,
  ApexLegend,
  ApexNoData,
  ApexPlotOptions,
  ApexTooltip,
  ApexXAxis,
  ApexYAxis,
  ChartComponent
} from 'ng-apexcharts';
import { debounceTime, distinctUntilChanged, map, merge, Observable, of, startWith, Subject, switchMap } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { InlineSvgComponent } from '../../../components/inline-svg/inline-svg.component';
import { FilterOperatorLabels, FilterOperatorsEnum, FilterOperatorSymbols } from '../../../models/general.models';
import {
  ExposureHistogramData,
  ResponseGeometry,
  RiskReport,
  RiskReportCollection
} from '../../../models/report.model';
import { FilterChipPipe } from '../../../pipes/filter-chip.pipe';
import { TruncatePipe } from '../../../pipes/truncate.pipe';
import { ErrorHandlerService } from '../../../services/error-handler.service';
import { NewReportService } from '../../../services/newReport.service';
import { NotificationService } from '../../../services/notification.service';
import {
  arraysEqualUnordered,
  compareArrays,
  getRiskColors,
  getValueByPath,
  isNumber,
  isYear,
  parseCaseToWords
} from '../../../utils';
import { MapComponent } from '../../risk-map/components/map/map.component';
import { MapCommunicatorService } from '../../risk-map/map-communicator.service';
import { AggregationModalDialogComponent } from './aggregation-modal-dialog/aggregation-modal-dialog.component';
import { DownloadReportDialogComponent } from './download-report-dialog/download-report-dialog.component';

export type ChartOptions = {
  series: ApexAxisChartSeries;
  chart: ApexChart;
  labels: any;
  plotOptions: ApexPlotOptions;
  legend: ApexLegend;
  colors: string[];
  tooltip: ApexTooltip;
  stroke: ApexStroke;
  fill: ApexFill;
  dataLabels: ApexDataLabels;
  yaxis: ApexYAxis;
  xaxis: ApexXAxis;
  noData: ApexNoData;
  grid: ApexGrid;
};

export type Filter = {
  key: {
    name: string;
    data_type: metaDataType;
    values?: Array<string>;
  };
  value: any;
  value2: any;
  type: FilterOperatorsEnum;
};
type metaDataType = 'string' | 'numeric';

@Component({
  selector: 'frmg-report',
  templateUrl: './AOISReport.component.html',
  styleUrls: ['./AOISReport.component.scss'],
  imports: [
    NgClass,
    MapComponent,
    MatButton,
    InlineSvgComponent,
    ReactiveFormsModule,
    MatFormField,
    MatSelect,
    MatOption,
    MatChipGrid,
    MatChipRow,
    MatChipRemove,
    MatIcon,
    MatAutocompleteTrigger,
    MatChipInput,
    MatAutocomplete,
    MatInput,
    MatTooltip,
    ChartComponent,
    MatPrefix,
    CdkDropListGroup,
    CdkDropList,
    CdkDrag,
    MatSlideToggle,
    MatTable,
    MatSort,
    MatColumnDef,
    MatHeaderCellDef,
    MatHeaderCell,
    MatSortHeader,
    MatCellDef,
    MatCell,
    MatMenuTrigger,
    MatMenu,
    MatMenuItem,
    MatHeaderRowDef,
    MatHeaderRow,
    MatRowDef,
    MatRow,
    AsyncPipe,
    DecimalPipe,
    TruncatePipe,
    FilterChipPipe
  ]
})
export class AOISReportComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() id!: string;

  @ViewChild('chart') chart: ChartComponent;
  @ViewChild('chipScroller', { static: true })
    chipScroller!: ElementRef<HTMLDivElement>;
  public chartOptions: Partial<ChartOptions>;
  public chartReady: boolean = false;

  public displayedColumns = ['name', 'avg_geometry_value', 'action'];
  public displayedColumnsDrag: string[] = [];
  public hiddenColumns: string[] = [];
  public metaKeys: string[] = [];
  public typedMetaKeys: Array<{
    name: string;
    data_type: metaDataType;
    values?: Array<string>;
  }> = [{
      name: 'avg_geometry_value',
      data_type: 'numeric'
    }];
  public reportDataFull: Array<ResponseGeometry> = [];
  public reportData: Array<RiskReportCollection> = [];
  public report: RiskReport;
  public totalElements = 0;
  public legends: number[] = [0, 1, 2, 3, 4];
  public colors: string[] = getRiskColors();
  public threshold: string = '0 - 100';
  public sizeTotal: string = '0 km<sup>2</sup>';
  public areaSearchCtrl: FormControl = new FormControl<string>('');
  public reportNameCtrl: FormControl = new FormControl<string>('');
  public loadingData = signal(false);
  public columnMenu: boolean = false;
  public chips: Array<Filter> = [
    {
      key: { name: 'avg_geometry_value', data_type: 'string' },
      value: 0,
      value2: 100,
      type: FilterOperatorsEnum.RANGE
    }
  ];
  public isNumericField: boolean;
  public currentSort: Sort;
  public blocks = [];
  public currentPage = 0;
  public showLeftArrow = false;
  public showRightArrow = false;
  private resizeObserver!: ResizeObserver;
  private readonly destroyRef = inject(DestroyRef);

  public separatorKeysCodes: number[] = [ENTER, COMMA];
  public valueCtrl = new FormControl('');
  public values = [];
  public filteredValues: Observable<string[]>;
  public selectedValuesChanged$ = new Subject<void>();
  @ViewChild('valueInput') valueInput: ElementRef<HTMLInputElement>;
  @ViewChild('auto') matAutocomplete: MatAutocomplete;
  @ViewChild(MatChipGrid) chipGrid: MatChipGrid;
  private exposureHistogramTypes = ['point', 'linestring', 'polygon'];

  get paginatedBlocks(): (any | 'add')[][] {
    const perPage = 4;
    const pages: (any | 'add')[][] = [];
    pages.push(['graph']);
    for (let i = 0; i < this.blocks.length; i += perPage) {
      const page = this.blocks.slice(i, i + perPage);
      pages.push(page);
    }

    const lastPage = pages[pages.length - 1];
    if (lastPage && lastPage.length < perPage) {
      lastPage.push('add');

      while (lastPage.length < perPage) lastPage.push(null);
    }

    return pages;
  }

  get totalPages(): number {
    return this.paginatedBlocks.length;
  }

  public filterMenu: boolean = false;
  public filterFormGroup: FormGroup;
  protected readonly FilterOperatorsEnum = FilterOperatorsEnum;
  public filterOperatorsEnum = Object.values(FilterOperatorsEnum);
  public filterOperators = Object.values(FilterOperatorsEnum);
  public filterOperatorLabels = FilterOperatorLabels;
  public filterOperatorSymbols = FilterOperatorSymbols;
  public chartColors = ['#EF8C8C', '#5EADB9', '#9B7EDA'];
  public labelColorMap: Record<string, string> = {
    POINT: this.chartColors[0],
    LINE: this.chartColors[1],
    POLYGON: this.chartColors[2]
  };
  public metadataValues = [];
  public chartType: number = 0;
  public collection: RiskReportCollection[];

  constructor(
    private reportService: NewReportService,
    private router: Router,
    private mapCommunicatorService: MapCommunicatorService,
    private notificationService: NotificationService,
    private cd: ChangeDetectorRef,
    private dialog: MatDialog,
    private fb: FormBuilder,
    private errorHandlerService: ErrorHandlerService
  ) {
    this.displayedColumnsDrag = this.displayedColumns.slice(1, -1);
    this.reportService.breadCrumbs.next([]);
    this.filterFormGroup = this.fb.group({
      key: [null, [Validators.required]],
      type: [null, [Validators.required]],
      value: [null, [Validators.required]],
      value2: null
    });
    this.filterFormGroup
      .get('key')
      .valueChanges.pipe(
        distinctUntilChanged(),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((key) => {
        if (key) {
          this.getAvailableOperations(key);
          this.getAvailableValues(key);

          this.setExposureValidators();

          const existingChip = this.chips.find(
            (chip) => chip?.key?.name === key?.name
          );
          if (existingChip) {
            console.log(existingChip);
            this.filterFormGroup.setValue(existingChip, { emitEvent: false });
            this.filterMenu = true;
            if (existingChip.type === FilterOperatorsEnum.IN) {
              this.values = existingChip.value;
              this.selectedValuesChanged$.next();
            }
          } else {
            this.filterFormGroup.get('type').setValue(null);
            this.filterFormGroup.get('value').setValue(null);
            this.filterFormGroup.get('value2').setValue(null);
            this.filterFormGroup
              .get('type')
              .updateValueAndValidity({ onlySelf: true, emitEvent: false });
            this.filterFormGroup
              .get('value')
              .updateValueAndValidity({ onlySelf: true, emitEvent: false });
            this.filterFormGroup
              .get('value2')
              .updateValueAndValidity({ onlySelf: true, emitEvent: false });
          }
        }
      });
    this.filterFormGroup
      .get('type')
      .valueChanges.pipe(
        distinctUntilChanged(),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((type: FilterOperatorsEnum) => {
        const value2Control = this.filterFormGroup.get('value2');
        if (type === FilterOperatorsEnum.RANGE) {
          value2Control.setValidators([Validators.required]);
        } else {
          value2Control.clearValidators();
          value2Control.setValue(null);
        }
        value2Control.updateValueAndValidity({
          onlySelf: true,
          emitEvent: false
        });
        this.updateNumericFieldStatus(type);
        this.setExposureValidators();
      });

    this.filteredValues = merge(
      this.valueCtrl.valueChanges.pipe(startWith('')),
      this.selectedValuesChanged$
    ).pipe(
      debounceTime(300),
      switchMap(() => {
        const value = this.valueCtrl.value || '';
        // Use first collection for metadata values
        const collectionId = this.report?.area_collections?.[0]?.id;
        if (!collectionId) {
          return of([]);
        }
        return this.reportService
          .getMetadataValues(
            this.id,
            collectionId,
            this.filterFormGroup.getRawValue().key.name,
            value
          )
          .pipe(
            map((results) =>
              results.filter((item) => !this.values.some((sel) => sel === item))
            )
          );
      })
    );
  }

  private updateNumericFieldStatus(operator: FilterOperatorsEnum): void {
    const numericOperators = [FilterOperatorsEnum.RANGE];

    this.isNumericField = numericOperators.includes(operator);
  }

  ngAfterViewInit() {
    const element = this.chipScroller.nativeElement;
    this.resizeObserver = new ResizeObserver(() => {
      this.updateArrowVisibility();
    });
    this.resizeObserver.observe(element);
  }

  ngOnDestroy(): void {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
  }

  @HostListener('window:resize')
  public onResize() {
    this.updateArrowVisibility();
  }

  ngOnInit() {
    const filters =
      localStorage.getItem(`${this.id}_filters`) &&
      (JSON.parse(localStorage.getItem(`${this.id}_filters`)) as Array<Filter>);
    if (filters && filters.length) {
      this.chips = filters;
    }
    this.getReport();

    this.chartOptions = {
      series: [
        {
          data: [2, 1, 12, 12, 12, 12, 25, 4],
          name: 'POINT'
        }
      ],
      labels: [
        'No Risk',
        'Low Risk',
        'Medium Risk',
        'High Risk',
        'Very High Risk'
      ],
      chart: {
        type: 'bar',
        toolbar: {
          show: false
        },
        height: '110%',
        width: '100%',
        events: {},
        zoom: {
          enabled: false
        },
        redrawOnParentResize: true
      },
      plotOptions: {
        bar: {
          borderRadius: 2,
          borderRadiusApplication: 'end',
          columnWidth: '100%'
        }
      },
      yaxis: {
        tickAmount: 4,
        forceNiceScale: true,
        decimalsInFloat: 0,
        title: {
          text: 'Number of AOIs'
        }
      },
      xaxis: {
        title: {
          text: 'Exposure',
          style: {
            fontSize: '14px',
            fontWeight: '500',
            fontFamily: 'Montserrat, sans-serif'
          }
        }
      },
      fill: {
        colors: ['#9B7EDA'],
        opacity: 1
      },
      dataLabels: {
        enabled: false
      },
      legend: {
        show: false
      },
      colors: this.colors,
      tooltip: {
        followCursor: false,
        cssClass: 'exposure-tooltip',
        x: {
          show: false
        },
        y: {
          formatter: (value) => {
            return `<span style="color:#5E84F7">${value}</span>`;
          },
          title: {
            formatter(seriesName) {
              return `Number of ${seriesName.toLowerCase()}`;
            }
          }
        },
        style: {
          fontFamily: 'Montserrat'
        }
      },
      stroke: {
        width: 1,
        colors: ['#6341AC']
      },
      noData: {
        text: 'No Data',
        offsetY: -80,
        style: {
          fontFamily: 'Montserrat',
          fontSize: '20px'
        }
      },
      grid: {
        padding: {
          left: -10,
          right: -10
        }
      }
    };

    this.areaSearchCtrl.valueChanges
      .pipe(
        debounceTime(500),
        distinctUntilChanged(),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((value) => {
        const config: any = { page: 1, size: 100 };
        const sort = this.currentSort;
        if (sort && sort.direction !== '') {
          if (sort.active === 'avg_geometry_value') {
            config.sort_by_metadata =
              (sort.direction === 'asc' ? '' : '-') + sort.active;
          } else {
            config.sort_by_metadata = sort.active;
            config.sort_direction = sort.direction;
          }
        } else {
          config.sort_by_metadata = 'name';
          config.sort_direction = 'asc';
        }
        config.name_search = value;
        const filter = this.getFilterStr();
        this.reportService
          .getReportAnalyse(this.id, config, filter)
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe((result) => {
            this.totalElements = result.items.length;
            const items = result.items.map((item) => item.geometry);
            if (
              !arraysEqualUnordered(this.reportDataFull, items, (item) =>
                JSON.stringify(item)
              )
            ) {
              this.reportDataFull = items;
            }
            // if (this.totalElements) {

            // }
            this.createTableData(result.items);
            this.loadingData.set(false);
          });
        // this.createTableData();
      });
  }

  public focusOn(element: RiskReportCollection, event: Event) {
    event.stopPropagation();
    if (!element.geometry) return;
    this.mapCommunicatorService.geometryToFocus.next({
      geometry: element.geometry.geometry_data,
      center: true
    });
  }

  public highlightOnHover(element: RiskReportCollection) {
    this.mapCommunicatorService.mapCommunicator.next(
      `hover_${element.geometry?.properties.area_id}`
    );
  }

  private setExposureValidators() {
    const key = this.filterFormGroup.get('key')!.value;
    const type = this.filterFormGroup.get('type')!.value as FilterOperatorsEnum;

    const v1 = this.filterFormGroup.get('value')!;
    const v2 = this.filterFormGroup.get('value2')!;

    const isExposure = key?.name === 'avg_geometry_value';
    const isRange = type === FilterOperatorsEnum.RANGE;

    v1.setValidators(
      isExposure
        ? [Validators.required, Validators.min(0), Validators.max(100)]
        : [Validators.required]
    );
    v1.updateValueAndValidity({ onlySelf: true, emitEvent: false });

    if (isRange) {
      v2.setValidators(
        isExposure
          ? [Validators.required, Validators.min(0), Validators.max(100)]
          : [Validators.required]
      );
    } else {
      v2.clearValidators();
      v2.setValue(null, { emitEvent: false });
    }
    v2.updateValueAndValidity({ onlySelf: true, emitEvent: false });
  }

  sortData(sort: Sort) {
    this.currentSort = sort;
    this.getReportInfo(sort);
  }

  private getReportInfo(sort?: Sort): void {
    this.loadingData.set(true);
    const skipCalc = !!sort;
    if (!sort && this.currentSort) {
      sort = this.currentSort;
    }
    const config: any = { page: 1, size: 100 };
    if (sort && sort.direction !== '') {
      // if (sort.active === 'exposure_value') {
      // config.sort_by_metadata = (sort.direction === 'asc' ? "" : "-") + sort.active
      // } else {
      config.sort_by_metadata = sort.active;
      config.sort_direction = sort.direction;
      // }
    } else {
      config.sort_by_metadata = 'name';
      config.sort_direction = 'asc';
    }
    const include = [];
    const include_metadata = [];
    this.displayedColumns.forEach((column) => {
      if (column == 'action' || column == 'name') return;
      if (column.includes('metadata.')) {
        include_metadata.push(column.replace('metadata.', ''));
      } else {
        include.push(column);
      }
    });
    const filter = this.getFilterStr();
    if (include.length) config.include = include.join(',');
    if (include_metadata.length)
      config.include_metadata = include_metadata.join(',');
    this.reportService
      .getReportAnalyse(this.id, config, filter)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result) => {
        this.totalElements = result.items.length;
        const items = result.items.map((item) => item.geometry);
        if (
          !arraysEqualUnordered(this.reportDataFull, items, (item) =>
            JSON.stringify(item)
          )
        ) {
          this.reportDataFull = items;
        }
        // if (this.totalElements) {

        // }
        this.createTableData(result.items);
        this.loadingData.set(false);
      });
    if (!skipCalc) this.getAllAggregationsAndCalc();
  }

  public updateAllData() {
    this.getChartData(this.exposureHistogramTypes[this.chartType]);
    this.getReportInfo();
  }

  private createTableData(data: RiskReportCollection[]): void {
    this.reportData = data;
  }

  private getReport(): void {
    this.loadingData.set(true);
    const displayedColumnsConfig = localStorage.getItem(
      `report_displayedColumns_${this.id}`
    );
    if (displayedColumnsConfig && displayedColumnsConfig.length) {
      this.displayedColumns = displayedColumnsConfig.split(',');
      this.displayedColumnsDrag = this.displayedColumns.slice(1, -1);
    }
    this.reportService
      .getReport(this.id)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        catchError(this.errorHandlerService.handle404And422('/report-table'))
      )
      .subscribe((report) => {
        this.report = report;
        this.reportService.getReportAnalyse(this.id).subscribe(collections => {
          this.collection = collections.items;
          if (
            this.collection.some(
              (collection) => collection.geometry_type === 'Point'
            )
          ) {
            this.chartType = 0;
          } else if (
            this.collection.some(
              (collection) => collection.geometry_type === 'LineString'
            )
          ) {
            this.chartType = 1;
          } else if (
            this.collection.some(
              (collection) => collection.geometry_type === 'Polygon'
            )
          ) {
            this.chartType = 2;
          }

          if (this.report?.id) {
            this.reportNameCtrl.setValue(this.report.name);
            this.threshold = `${this.report.lowest_threshold} - ${this.report.highest_threshold}`;
            const collectionId = this.collection?.[0]?.id;
            if (collectionId) {
              this.reportService
                .getMetadataKeysProperties(report.id, collectionId)
                .pipe(takeUntilDestroyed(this.destroyRef))
                .subscribe((typedKeys) => {
                  const metaKeys = typedKeys;
                  // const metaKeys = typedKeys.map((key) => key.name);
                  // this.typedMetaKeys = typedKeys;
                  // this.sortFilterOptions();
                  this.hiddenColumns = metaKeys.filter((key: string) => {
                    return (
                      !this.displayedColumns.includes(key)
                    );
                  });
                  this.metaKeys = [
                    'avg_geometry_value',
                    // ...metaKeys.filter((key: string) => {
                    //   return key.includes('metadata.');
                    // })
                    ...metaKeys
                  ];
                });
            } else {
              console.warn('No collections found for report');
            }
            this.getChartData(this.exposureHistogramTypes[this.chartType]);
            this.getReportInfo();
          }
        });
      });
  }

  public getChartData(type: string) {
    this.reportService
      .getReportScoreByType(this.report.id, type, this.chips)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((scores) => {
        this.calculateChartData(scores);
      });
  }

  private calculateChartData(scores: ExposureHistogramData) {
    let max = Math.max(...scores.counts);
    max = max + (4 - (max % 4));
    this.chartOptions.yaxis = {
      tickAmount: 4,
      max,
      min: 0,
      // forceNiceScale: true,
      decimalsInFloat: 0,
      labels: {
        offsetY: -5
      },
      title: {
        text: 'Number of AOIs',
        style: {
          fontSize: '12px',
          fontWeight: '500',
          fontFamily: 'Montserrat, sans-serif'
        }
      }
    };
    let labels = [];
    const data = scores.counts;
    let seriesName;
    switch (this.exposureHistogramTypes[this.chartType]) {
      case 'point':
        seriesName = 'Points';
        break;
      case 'linestring':
        seriesName = 'Lines';
        break;
      case 'polygon':
        seriesName = 'Polygons';
        break;
      default:
        seriesName = 'AOIs';
        break;
    }
    if (data.length) {
      const series = [{
        data: data.map((d, i) => {
          const startSeg = +scores.bins[i].split('-')[0];
          const pos = scores.bin_edges.findIndex(edge => edge == startSeg);
          return {
            x: pos + 0.5,
            y: d
          };
        }),
        name: seriesName
      }];
      series[0].data.push({ x: (series[0].data.length + 0.5), y: 0 });
      this.chartOptions.series = series;
      labels = scores.bin_edges;
    } else {
      this.chartOptions.series = [];
      labels = ['0', '20', '40', '60', '80', '100'];
    }
    const labelsInfo = {
      style: {
        fontFamily: 'Montserrat, sans-serif'
      },
      formatter: (x) => {
        return labels[x];
      }
    };
    this.chartOptions.xaxis = {
      min: 0,
      max: labels.length || 6,
      tickAmount: labels.length || 6,
      tickPlacement: 'on',
      title: {
        text: 'Exposure',
        offsetY: -10,
        style: {
          fontSize: '14px',
          fontWeight: '500',
          fontFamily: 'Montserrat, sans-serif'
        }
      },
      categories: [],
      labels: labelsInfo
    };

    this.chartOptions.labels = labelsInfo;
    // this.chartLabel = "Exposure";
    // this.scoreTotal = Object.entries(scores).map((el) => +el[0] * el[1]).reduce(this.add, 0);
    // this.chartOptions.series = Object.values(scores);
    // this.avgScore = (this.scoreTotal / Object.values(scores).reduce(this.add, 0)).toFixed(1);
    this.chartReady = true;
  }

  public downloadArea(id: string, filename: string = 'report'): void {
    const dialog = this.dialog.open(DownloadReportDialogComponent, {
      data: {
        areas: [id],
        metadata: this.metaKeys,
        selected: this.displayedColumns.filter((displayed) =>
          this.metaKeys.includes(displayed)
        )
      }
    });

    dialog
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((resp) => {
        const filter = {
          include: [],
          include_metadata: []
        };
        resp.meta.forEach((key) => {
          if (key.includes('metadata.')) {
            filter.include_metadata.push(key.replace('metadata.', ''));
          } else {
            filter.include.push(key);
          }
        });
        let _urlFilter = '';
        _urlFilter += filter.include.length
          ? `include=${filter.include.join(',')}`
          : '';
        _urlFilter += filter.include_metadata.length
          ? `&include_metadata=${filter.include_metadata.join(',')}`
          : '';
        if (resp.type === 'pdf') {
          this.reportService
            .getAreaReportPdf(this.id, id, filename)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((data) => {
              this.notificationService.updateDownload({
                fileName: `${filename}.pdf`,
                percentage: data.progress,
                total: data.total,
                loaded: data.loaded
              });
            });
        } else {
          this.reportService
            .getAreaReportXLSX(this.id, id, filename)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((data) => {
              this.notificationService.updateDownload({
                fileName: `${filename}.xlsx`,
                percentage: data.progress,
                total: data.total,
                loaded: data.loaded
              });
            });
        }
      });
  }

  public addAggregation(): void {
    this.reportService
      .getMetadataKeys(this.report.id, this.collection[0].id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((resp) => {
        const dialog = this.dialog.open(AggregationModalDialogComponent, {
          data: {
            metadata_fields: resp,
            exposure_fields: ['avg_geometry_value']
          }
        });
        dialog
          .afterClosed()
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe((resp) => {
            if (resp) {
              this.reportService
                .createAggregationConfig(resp)
                .pipe(takeUntilDestroyed(this.destroyRef))
                .subscribe((_) => {
                  this.getAllAggregationsAndCalc(true);
                });
            }
          });
      }, _ => {
        console.log('error');
        const dialog = this.dialog.open(AggregationModalDialogComponent, {
          data: {
            metadata_fields: [],
            exposure_fields: [{ path: 'avg_geometry_value', name: 'Exposure' }]
          }
        });
        dialog
          .afterClosed()
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe((resp) => {
            if (resp) {
              this.reportService
                .createAggregationConfig(resp)
                .pipe(takeUntilDestroyed(this.destroyRef))
                .subscribe((_) => {
                  this.getAllAggregationsAndCalc(true);
                });
            }
          });
      });
  }

  public getAllAggregationsAndCalc(scrollToLast?: boolean) {
    this.reportService
      .gerReportAggregations(this.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((configList) => {
        console.log(configList);
        this.blocks = configList;
        const filter = {
          filter_config: {}
        };
        this.chips.forEach((chip) => {
          if (chip.type === FilterOperatorsEnum.RANGE) {
            filter.filter_config[chip.key.name + FilterOperatorsEnum.GTE] =
              chip.value;
            filter.filter_config[chip.key.name + FilterOperatorsEnum.LTE] =
              chip.value2;
          } else {
            filter.filter_config[chip.key.name + chip.type] = chip.value;
          }
        });
        this.reportService
          .calculateAllAggregations()
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe((calculated) => {
            calculated.results.forEach((config) => {
              const chipIndex = this.blocks.findIndex(
                (chip) => chip.id === config.config_id
              );
              if (chipIndex !== -1) {
                this.blocks[chipIndex].calc = config.calculation;
                if (scrollToLast) {
                  this.currentPage = this.totalPages - 1;
                }
              }
            });
          });
      });
  }

  public deleteAggregation(config_id: string, page: any): void {
    this.reportService
      .deleteAggregationConfigs(config_id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((_) => {
        const index = this.blocks.findIndex(
          (config) => config.id === config_id
        );
        this.blocks.splice(index, 1);
        const amountOfCard = page.filter((item) => item && item.id).length;
        if (amountOfCard == 1) {
          this.scroll('left');
        }
      });
  }

  public openAnalysisModal(analysis: RiskReportCollection) {
    if (analysis.geometry_type !== 'linestring') {
      this.reportService.detailsType.next(analysis.geometry_type);
      this.router.navigate([
        `report/${this.id}/collection/${analysis.id}/details/${analysis.geometry?.id}`
      ]);
    } else {
      this.reportService.detailsType.next(analysis.geometry_type);
      this.router.navigate([`report/${this.id}/collection/${analysis.id}`]);
    }
  }

  public openAnalysisModalById(analysisId: string) {
    const analysis = this.reportData.find(
      (analysis) => analysis.id === analysisId
    );
    if (analysis) this.openAnalysisModal(analysis);
  }

  public drop(event: CdkDragDrop<string[]>) {
    moveItemInArray(
      event.container.data,
      event.previousIndex,
      event.currentIndex
    );
    this.displayedColumns = ['name', ...this.displayedColumnsDrag, 'action'];
    localStorage.setItem(
      `report_displayedColumns_${this.report.id}`,
      this.displayedColumns.join(',')
    );
    this.sortFilterOptions();
  }

  private sortFilterOptions() {
    this.typedMetaKeys.sort((a, b) => {
      if (
        this.displayedColumns.includes(a.name) &&
        !this.displayedColumns.includes(b.name)
      )
        return -1;
      if (
        !this.displayedColumns.includes(a.name) &&
        this.displayedColumns.includes(b.name)
      )
        return 1;
      return 0;
    });
  }

  public toggleColumn(index: number, column: string, show: boolean) {
    if (show) {
      this.hiddenColumns.push(column);
      this.displayedColumnsDrag.splice(index, 1);
    } else {
      this.displayedColumnsDrag.push(column);
      this.hiddenColumns.splice(index, 1);
    }
    this.displayedColumns = ['name', ...this.displayedColumnsDrag, 'action'];
    localStorage.setItem(
      `report_displayedColumns_${this.report.id}`,
      this.displayedColumns.join(',')
    );
    this.sortFilterOptions();
    this.cd.detectChanges();
  }

  public toggleColumnMenu() {
    this.columnMenu = !this.columnMenu;
  }

  public toggleFilterMenu(apply?: boolean, index?: number) {
    console.log(apply, index);
    if (apply) {
      const filterValues = this.filterFormGroup.value;
      const filterIndex = this.chips.findIndex(
        (chip) => chip.key.name === filterValues.key.name
      );
      if (filterIndex !== -1) {
        this.chips[filterIndex] = filterValues;
        localStorage.setItem(`${this.id}_filters`, JSON.stringify(this.chips));
      } else {
        this.chips.push(filterValues);
        localStorage.setItem(`${this.id}_filters`, JSON.stringify(this.chips));
      }
      this.updateAllData();
      this.filterFormGroup.setValue({
        key: null,
        type: null,
        value: null,
        value2: null
      });
      this.clearFormErrors(this.filterFormGroup);
      this.filterMenu = false;
    } else if (index !== undefined) {
      this.filterFormGroup.setValue(this.chips[index]);
      console.log(this.chips[index]);
      this.filterMenu = true;
      if (this.chips[index].type === FilterOperatorsEnum.IN) {
        this.values = this.chips[index].value;
        this.selectedValuesChanged$.next();
      }
    } else {
      this.filterFormGroup.setValue({
        key: null,
        type: null,
        value: null,
        value2: null
      });
      this.values = [];
      this.clearFormErrors(this.filterFormGroup);
      this.filterMenu = !this.filterMenu;
    }

    this.cd.detectChanges();
  }

  private clearFormErrors(formGroup: FormGroup | FormArray): void {
    Object.keys(formGroup.controls).forEach((key) => {
      const control = formGroup.get(key);

      if (control instanceof FormGroup || control instanceof FormArray) {
        this.clearFormErrors(control);
      } else if (control instanceof FormControl) {
        control.setErrors(null);
        control.markAsPristine();
        control.markAsUntouched();
      }
    });

    formGroup.setErrors(null);
    formGroup.markAsPristine();
    formGroup.markAsUntouched();
  }

  public deleteFilter(index: number) {
    this.chips.splice(index, 1);
    localStorage.setItem(`${this.id}_filters`, JSON.stringify(this.chips));
    this.updateAllData();
  }

  public getAvailableOperations(key: {
    name: string;
    data_type: metaDataType;
  }) {
    let operators;
    if (key && key.name == 'avg_geometry_value') {
      operators = [FilterOperatorsEnum.RANGE];
    } else {
      operators = this.filterOperatorsEnum.filter((agg) => {
        if (key && key.data_type === 'string') {
          return agg == FilterOperatorsEnum.IN;
        } else {
          return (
            agg === FilterOperatorsEnum.RANGE || agg === FilterOperatorsEnum.EQ
          );
        }
      });
    }
    if (compareArrays(this.filterOperators, operators)) return;
    this.filterOperators = operators;
  }

  public getAvailableValues(key: {
    name: string;
    data_type: metaDataType;
    values?: Array<string>;
  }) {
    if (key.data_type === 'numeric') {
      const typeCtrl = this.filterFormGroup.get('type');
      if (typeCtrl.value === FilterOperatorsEnum.IN) {
        this.filterFormGroup.get('type').setValue(null);
      }
      return;
    }

    if (key.values) {
      this.metadataValues = key.values;
    } else {
      // Use first collection for metadata values
      const collectionId = this.report?.area_collections?.[0]?.id;
      if (collectionId) {
        this.reportService
          .getMetadataValues(this.id, collectionId, key.name)
          .subscribe(
            (values) => {
              this.metadataValues = values;
              const metadataIndex = this.typedMetaKeys.findIndex(
                (metaKey) => metaKey.name === key.name
              );
              this.typedMetaKeys[metadataIndex].values = values;
            },
            (_) => {
              this.metadataValues = [];
            }
          );
      } else {
        console.warn('No collections found for report');
        this.metadataValues = [];
      }
    }
    this.filterFormGroup.get('type').setValue(FilterOperatorsEnum.IN);
  }

  public chipsTrackBy(index: number, key: { name: string; data_type: string }) {
    return key.name;
  }

  public compareKeys(
    key1: { name: string; data_type: string },
    key2: { name: string; data_type: string }
  ): boolean {
    return key1?.name === key2?.name;
  }

  public getFilterStr() {
    let filterStr = '';
    this.chips.forEach((chip, index) => {
      if (index !== 0) filterStr += '&';
      if (chip.type === FilterOperatorsEnum.RANGE) {
        filterStr += `${chip.key.name}${FilterOperatorsEnum.GTE}=${chip.value}`;
        filterStr += `&${chip.key.name}${FilterOperatorsEnum.LTE}=${chip.value2}`;
      } else {
        filterStr += `${chip.key.name}${chip.type}=${chip.value}`;
      }
    });
    return encodeURI(filterStr);
  }

  public getAreaIconType(area: RiskReportCollection): string {
    switch (area.geometry?.geometry_data.type) {
      case 'Circle':
      case 'Polygon':
      case 'MultiPolygon':
        return 'polygon-space.svg';
      case 'Point':
      case 'MultiPoint':
        return 'point.svg';
      case 'LineString':
      case 'MultiLineString':
      case 'LinearRing':
      default:
        return 'line.svg';
    }
  }

  public columnHeader(column: string) {
    switch (column) {
      case 'name':
        return 'Area Name';
      case 'avg_geometry_value':
        return 'Exposure';
      case 'geometry_type':
        return 'AOI Type';
      case 'risk_percentage':
        return 'Risk Percentage';
      case 'action':
        return '';
      default:
        return parseCaseToWords(column.replace('metadata.', ''));
    }
  }

  public isColumnRemovable(column: string): boolean {
    switch (column) {
      case 'name':
      case 'first_risk_score':
      case 'avg_geometry_value':
      case 'risk_percentage':
      case 'action':
        return false;
      default:
        return true;
    }
  }

  public onScroll() {
    this.updateArrowVisibility();
  }

  public updateArrowVisibility() {
    const scroller = this.chipScroller.nativeElement;
    this.showLeftArrow = scroller.scrollLeft > 0;
    this.showRightArrow =
      Math.ceil(scroller.scrollLeft + scroller.clientWidth) <
      scroller.scrollWidth;
  }

  public scrollChips(direction: 'left' | 'right') {
    const scroller = this.chipScroller.nativeElement;
    const scrollAmount = 50;

    scroller.scrollBy({
      left: direction === 'left' ? -scrollAmount * 3 : scrollAmount * 3,
      behavior: 'smooth'
    });
    setTimeout(() => this.updateArrowVisibility(), 500);
  }

  public getTransform(): string {
    return `translateX(-${this.currentPage * 100}%)`;
  }

  public scroll(direction: 'left' | 'right'): void {
    if (direction === 'left' && this.currentPage > 0) this.currentPage--;
    if (direction === 'right' && this.currentPage < this.totalPages - 1)
      this.currentPage++;
  }

  public goToChart() {
    this.currentPage = 0;
  }

  public trackPage = (index: number, _: any) => index;
  protected readonly isNumber = isNumber;
  protected readonly color = color;
  protected readonly isYear = isYear;

  removeValue(keyword: string) {
    const index = this.values.indexOf(keyword);
    if (index >= 0) {
      this.values.splice(index, 1);
    }
    this.selectedValuesChanged$.next();
  }

  selectChart(index: number) {
    if (this.chartType === index) return;
    this.chartType = index;
    this.getChartData(this.exposureHistogramTypes[index]);
  }

  selectedValue(event: MatAutocompleteSelectedEvent): void {
    if (this.valueInput) {
      this.values.push(event.option.viewValue);
      this.valueInput.nativeElement.value = '';
      this.valueCtrl.setValue('');
      this.filterFormGroup.get('value').updateValueAndValidity();
      this.filterFormGroup.get('value').markAsPristine();
      this.filterFormGroup.get('value').markAsUntouched();
      this.filterFormGroup.setErrors(null);
    }
  }

  protected readonly getValueByPath = getValueByPath;
}
